/**
 * Short source codes: a tiny `?s=` param that stands in for a full UTM set.
 * Used where UTMs do not fit — mainly printed QR codes, where bytes cost scan reliability. TRAV-3957.
 */

/** Segment's `context.campaign` shape. `name` is its field for `utm_campaign`. */
export type SegmentCampaign = Readonly<{
  source: string;
  medium: string;
  name: string;
}>;

/** Query param carrying the short source code. */
export const SHORT_SOURCE_PARAM = 's';

/**
 * Short code -> the campaign it means. Set by whatever builds the link (today `card_service`).
 * Codes are permanent once printed: only add entries, never remove or reuse one.
 */
export const SHORT_SOURCE_CAMPAIGNS: Readonly<Record<string, SegmentCampaign>> = {
  // Printed invitations
  p: { source: 'print', medium: 'paper', name: 'invitation' },
};

/** A Map, not object indexing — `?s=toString` would otherwise resolve off the prototype. */
const SHORT_SOURCE_LOOKUP: ReadonlyMap<string, SegmentCampaign> = new Map(Object.entries(SHORT_SOURCE_CAMPAIGNS));

/** Every param Segment maps into `context.campaign`, not just the three a code fills. */
const INBOUND_UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

/** Whether the URL already has UTMs. Segment builds `context.campaign` from those itself. */
export const hasInboundUtm = (search: string): boolean => {
  try {
    const params = new URLSearchParams(search);
    return INBOUND_UTM_PARAMS.some(key => !!params.get(key));
  } catch {
    return false;
  }
};

/**
 * Resolves `?s=<code>` into a campaign, or `undefined` if the code is missing, unknown, or UTMs are present.
 * All or nothing: a half-filled campaign would label an email guest as paper.
 */
export const resolveShortSourceCampaign = (search: string): SegmentCampaign | undefined => {
  try {
    if (hasInboundUtm(search)) return undefined;

    const code = new URLSearchParams(search).get(SHORT_SOURCE_PARAM);
    return code ? SHORT_SOURCE_LOOKUP.get(code) : undefined;
  } catch {
    return undefined;
  }
};
